// Rejestr badania: znaleziska i akcje ze wszystkich ośrodków badania, pogrupowane po ośrodkach. Tylko odczyt;
// edycja w rejestrze ośrodka (link przy każdej grupie i wierszu).
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchStudyIssues, type SiteIssue } from "../../api/siteIssuesApi";
import { ActionStatus } from "../../types/reportActions";
import { cn } from "../../lib/utils";
import { ISSUE_FILTERS, IssueFilter, formatDay, matchesFilter } from "./siteIssueRows";
import { RowsSkeleton } from "../../components/ui/Skeleton";

const Tag = ({ children, tone }: { children: string; tone: "red" | "amber" | "muted" }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-medium whitespace-nowrap",
      tone === "red" && "bg-[var(--sc-red-bg)] text-[var(--sc-red)]",
      tone === "amber" && "bg-[var(--sc-amber-bg)] text-[#C07810]",
      tone === "muted" && "bg-[var(--sc-bg)] text-[var(--sc-muted)]"
    )}
  >
    {children}
  </span>
);

const isOpen = (i: SiteIssue) => i.status === ActionStatus.Open;
const isSerious = (i: SiteIssue) => i.serious || i.seriousBreach;

export const matchesSearch = (i: SiteIssue, phrase: string) => {
  const q = phrase.trim().toLowerCase();
  if (!q) return true;
  return [i.participantNumber, i.description, i.category, i.responsiblePerson, i.actionsTaken, i.visitNumber]
    .some((v) => v?.toLowerCase().includes(q));
};

export const StudySiteIssuesPage = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery(["studySiteIssues", studyId], () => fetchStudyIssues(studyId!), {
    enabled: !!studyId, retry: false,
  });

  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<IssueFilter>(() => {
    const f = searchParams.get("filter");
    return f && (ISSUE_FILTERS as readonly string[]).includes(f) ? (f as IssueFilter) : "all";
  });
  const [resortId, setResortId] = useState<string>("");
  const [search, setSearch] = useState("");

  const items = useMemo(() => data?.items ?? [], [data]);
  // Liczniki na przyciskach filtrów: w zakresie wybranego ośrodka i wyszukiwania
  const scoped = useMemo(
    () => items.filter((i) => (!resortId || i.resortId === resortId) && matchesSearch(i, search)),
    [items, resortId, search]
  );
  const counts = {
    all: scoped.length,
    open: scoped.filter(isOpen).length,
    review: scoped.filter((i) => i.needsReview).length,
    seriousOpen: scoped.filter((i) => isOpen(i) && isSerious(i)).length,
    closed: scoped.filter((i) => !isOpen(i)).length,
  };
  const FILTERS: { id: IssueFilter; label: string }[] = [
    { id: "all", label: `Wszystkie (${counts.all})` },
    { id: "open", label: `Otwarte (${counts.open})` },
    { id: "review", label: `Do weryfikacji (${counts.review})` },
    { id: "seriousOpen", label: `Poważne otwarte (${counts.seriousOpen})` },
    { id: "closed", label: `Zamknięte (${counts.closed})` },
  ];

  // Grupy po ośrodkach (kolejność ośrodków z backendu: alfabetycznie), tylko z pasującymi pozycjami
  const groups = useMemo(() => {
    const shown = scoped.filter((i) => matchesFilter(i, filter));
    return (data?.resorts ?? [])
      .map((r) => {
        const all = items.filter((i) => i.resortId === r.id);
        return {
          ...r,
          rows: shown.filter((i) => i.resortId === r.id),
          open: all.filter(isOpen).length,
          review: all.filter((i) => i.needsReview).length,
        };
      })
      .filter((g) => g.rows.length > 0);
  }, [data, items, scoped, filter]);

  return (
    <div className="flex-1 overflow-y-auto bg-[var(--sc-bg)] px-4 md:px-12 py-9">
      <div className="mb-6">
        {data && (
          <Link to={`/study/${data.studyId}`} className="text-[12px] text-[var(--sc-blue)] hover:underline">
            ← {data.studyName}
          </Link>
        )}
        <h1 className="text-[28px] font-light tracking-tight text-[var(--sc-text)] leading-none mt-2">
          Rejestr badania{data ? `: ${data.studyName}` : ""}
        </h1>
        <p className="text-[13px] text-[var(--sc-muted)] mt-1">
          Znaleziska i akcje ze wszystkich ośrodków badania. Pozycje edytuje się w rejestrze ośrodka.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "text-[12px] px-3 py-1.5 rounded-full border transition-colors",
              filter === f.id
                ? "bg-[var(--sc-amber)] border-[var(--sc-amber)] text-[#0F0F0F] font-medium"
                : "bg-[var(--sc-surface)] border-[var(--sc-border)] text-[var(--sc-muted)] hover:text-[var(--sc-text)]"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select
          aria-label="Ośrodek"
          value={resortId}
          onChange={(e) => setResortId(e.target.value)}
          className="h-8 rounded-[8px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-2 text-[12px] text-[var(--sc-text)]"
        >
          <option value="">Wszystkie ośrodki ({data?.resorts.length ?? 0})</option>
          {(data?.resorts ?? []).map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Szukaj: nr uczestnika, opis, osoba…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-[280px] max-w-full rounded-[8px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-2.5 text-[12px] text-[var(--sc-text)] placeholder:text-[var(--sc-muted)]"
        />
      </div>

      {isLoading && <RowsSkeleton rows={8} />}
      {error && <p className="text-[13px] text-[var(--sc-red)]">Nie udało się wczytać rejestru badania.</p>}
      {data && groups.length === 0 && (
        <p className="text-[13px] text-[var(--sc-muted)]">Brak pozycji dla wybranych filtrów.</p>
      )}

      {groups.map((g) => {
        const registerUrl = `/resorts/${g.id}/issues`;
        return (
          <section key={g.id} className="mb-5 rounded-xl border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-[var(--sc-border)] bg-[var(--sc-bg)]">
              <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--sc-text)]">{g.name}</span>
              {g.open > 0 && <Tag tone="amber">{`${g.open} otwarte`}</Tag>}
              {g.review > 0 && <Tag tone="red">{`${g.review} do weryfikacji`}</Tag>}
              <span className="text-[11px] text-[var(--sc-muted)]">· pokazano {g.rows.length}</span>
              <div className="flex-1" />
              <Link to={registerUrl} className="text-[11px] text-[var(--sc-blue)] hover:underline whitespace-nowrap">
                Rejestr ośrodka →
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.05em] text-[var(--sc-muted)]">
                    <th className="px-4 py-2 font-medium w-[110px]">Nr uczestnika</th>
                    <th className="px-2 py-2 font-medium w-[150px]">Czego dotyczy</th>
                    <th className="px-2 py-2 font-medium">Opis</th>
                    <th className="px-2 py-2 font-medium w-[90px]">Identyfikacja</th>
                    <th className="px-2 py-2 font-medium w-[100px]">Status</th>
                    <th className="px-2 py-2 font-medium w-[140px]">Odpowiedzialny</th>
                    <th className="px-4 py-2 font-medium w-[150px]">Oznaczenia</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((i) => (
                    <tr
                      key={i.id}
                      onClick={() => navigate(`${registerUrl}?filter=${i.needsReview ? "review" : isOpen(i) ? "open" : "closed"}`)}
                      title={i.reviewNote ? `Do weryfikacji: ${i.reviewNote}` : undefined}
                      className="border-t border-[var(--sc-border)] align-top cursor-pointer hover:bg-[var(--sc-bg)] transition-colors"
                    >
                      <td className="px-4 py-2 font-medium text-[var(--sc-text)] break-words">{i.participantNumber || "—"}</td>
                      <td className="px-2 py-2 text-[10px] uppercase tracking-[0.04em] text-[var(--sc-muted)] break-words">{i.category ?? ""}</td>
                      <td className="px-2 py-2 text-[var(--sc-text)]">
                        <div className="line-clamp-3 whitespace-pre-line" title={i.description}>{i.description}</div>
                        {i.actionsTaken && (
                          <div className="mt-1 text-[11px] text-[var(--sc-muted)] line-clamp-2" title={i.actionsTaken}>
                            Działania: {i.actionsTaken}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[var(--sc-muted)] whitespace-nowrap">{formatDay(i.identificationDate)}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {isOpen(i) ? (
                          <span className="text-[#C07810]">otwarte</span>
                        ) : (
                          <span className="text-[var(--sc-muted)]">zamknięte {formatDay(i.closedDate)}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[var(--sc-muted)] break-words">{i.responsiblePerson ?? ""}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {i.needsReview && <Tag tone="red">do weryfikacji</Tag>}
                          {i.seriousBreach && <Tag tone="red">Serious Breach</Tag>}
                          {i.serious && <Tag tone="amber">poważne</Tag>}
                          {i.potentialDeviation && <Tag tone="muted">PD</Tag>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
};
